import { useCallback, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowUpRight, Hand, Maximize2, MicOff, Mic, Minimize2, MonitorOff, MonitorUp, PhoneOff, Video, VideoOff } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Google-Meet-style floating call window, using the Document Picture-in-Picture API
 * (`window.documentPictureInPicture.requestWindow`, Chrome / Edge 116+). The window is an always-on-top
 * mini document; we render a compact React UI into it with a portal, after copying the app's stylesheets
 * so the Tailwind classes work there. Videos reuse the room's live MediaStreams (a stream can feed several
 * <video> elements); audio keeps playing from the meeting tab.
 */

interface DocumentPictureInPictureApi {
    requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
    readonly window: Window | null;
}

function docPip(): DocumentPictureInPictureApi | null {
    const api = (window as unknown as { documentPictureInPicture?: DocumentPictureInPictureApi }).documentPictureInPicture;
    return api ?? null;
}

export function isDocumentPipSupported(): boolean {
    return typeof window !== "undefined" && docPip() !== null;
}

/**
 * Default pop-out size — Teams' compact meeting window, about two thirds of the old 380×320 so it covers
 * less of whatever is being presented.
 */
export const POPOUT_SIZE = { width: 248, height: 224 } as const;
/** Height of the minimised bar (window chrome included). */
const POPOUT_MINIMISED_HEIGHT = 84;

/** Opens the floating window (needs a user gesture, or Chrome's auto-PiP media-session action). */
export async function openDocumentPip(title: string, width: number = POPOUT_SIZE.width, height: number = POPOUT_SIZE.height): Promise<Window> {
    const api = docPip();
    if (!api) throw new Error("Document Picture-in-Picture is not supported");
    const win = api.window ?? await api.requestWindow({ width, height });
    const doc = win.document;
    copyStyles(doc);
    doc.title = title;
    doc.documentElement.classList.add("dark");
    doc.body.className = "m-0 overflow-hidden bg-zinc-950 text-white antialiased";
    return win;
}

function copyStyles(target: Document) {
    for (const sheet of Array.from(document.styleSheets)) {
        try {
            const css = Array.from(sheet.cssRules).map((r) => r.cssText).join("\n");
            const style = target.createElement("style");
            style.textContent = css;
            target.head.appendChild(style);
        } catch {
            // Cross-origin sheet (a font CSS): link it instead.
            if (sheet.href) {
                const link = target.createElement("link");
                link.rel = "stylesheet";
                link.href = sheet.href;
                target.head.appendChild(link);
            }
        }
    }
}

export interface PopoutTile {
    id: string;
    label: string;
    stream: MediaStream | null;
    videoOn: boolean;
    muted: boolean;
    handRaised: boolean;
    speaking: boolean;
    isSelf?: boolean;
}

export interface MeetingPopoutProps {
    win: Window;
    subject: string;
    main: PopoutTile | null;
    others: PopoutTile[];
    /** "You are presenting" / "Bijo is presenting" / null. */
    presenting: string | null;
    audioOnly: boolean;
    micOn: boolean;
    camOn: boolean;
    camDisabled: boolean;
    sharing: boolean;
    handRaised: boolean;
    reconnecting: boolean;
    onToggleMic: () => void;
    onToggleCam: () => void;
    onToggleShare: () => void;
    onToggleHand: () => void;
    onLeave: () => void;
    onBackToTab: () => void;
}

export function MeetingPopout(props: MeetingPopoutProps) {
    return createPortal(<PopoutBody {...props} />, props.win.document.body);
}

function PopoutBody({
    win, subject, main, others, presenting, audioOnly, micOn, camOn, camDisabled, sharing, handRaised, reconnecting,
    onToggleMic, onToggleCam, onToggleShare, onToggleHand, onLeave, onBackToTab
}: MeetingPopoutProps) {
    // Teams-style minimise: collapse to a slim bar (subject, mic, restore, leave) so it stays out of the way
    // of what's being presented. The click is the user activation resizeTo() needs in a PiP window.
    const [minimised, setMinimised] = useState(false);
    const [restoreSize, setRestoreSize] = useState<{ w: number; h: number } | null>(null);
    const minimise = () => {
        setRestoreSize({ w: win.outerWidth, h: win.outerHeight });
        setMinimised(true);
        try { win.resizeTo(win.outerWidth, POPOUT_MINIMISED_HEIGHT); } catch { /* resize not allowed: layout still collapses */ }
    };
    const restore = () => {
        setMinimised(false);
        const size = restoreSize ?? { w: POPOUT_SIZE.width, h: POPOUT_SIZE.height };
        try { win.resizeTo(size.w, size.h); } catch { /* ignore */ }
    };

    if (minimised) {
        return (
            <div className="flex h-screen w-screen items-center gap-1.5 bg-zinc-950 px-1.5 text-white">
                <span className="min-w-0 flex-1 truncate text-[11px] font-semibold" title={subject}>
                    {presenting ? <MonitorUp className="mr-1 inline h-3 w-3 text-sky-300" aria-label={presenting} /> : null}
                    {subject}
                </span>
                <PopButton label={micOn ? "Mute" : "Unmute"} danger={!micOn} onClick={onToggleMic}>
                    {micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                </PopButton>
                <PopButton label="Restore" onClick={restore}>
                    <Maximize2 className="h-4 w-4" />
                </PopButton>
                <button
                    type="button"
                    onClick={onLeave}
                    aria-label="Leave"
                    title="Leave"
                    className="flex h-8 w-8 items-center justify-center rounded-md bg-red-600 hover:bg-red-500"
                >
                    <PhoneOff className="h-4 w-4" />
                </button>
            </div>
        );
    }

    return (
        <div className="flex h-screen w-screen flex-col gap-1.5 bg-zinc-950 p-1.5 text-white">
            <div className="flex min-w-0 items-center gap-2 px-1 text-[11px]">
                <span className="min-w-0 flex-1 truncate font-semibold">{subject}</span>
                {reconnecting ? <span className="shrink-0 rounded-full bg-amber-500/25 px-1.5 text-amber-200">Reconnecting…</span> : null}
                {presenting ? (
                    <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-primary/30 px-1.5 text-sky-100" title={presenting}>
                        <MonitorUp className="h-3 w-3" />
                    </span>
                ) : null}
                <button
                    type="button"
                    onClick={minimise}
                    aria-label="Minimise"
                    title="Minimise"
                    className="flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-zinc-800"
                >
                    <Minimize2 className="h-3.5 w-3.5" />
                </button>
            </div>
            <div className="relative min-h-0 flex-1">
                {main ? <PopTile tile={main} large audioOnly={audioOnly} /> : (
                    <div className="flex h-full items-center justify-center rounded-md bg-zinc-900 text-xs text-white/60">Waiting for others…</div>
                )}
            </div>
            {others.length > 0 ? (
                <div className="flex h-16 shrink-0 gap-1.5 overflow-hidden">
                    {others.map((t) => (
                        <div key={t.id} className="h-full min-w-0 flex-1">
                            <PopTile tile={t} audioOnly={audioOnly} />
                        </div>
                    ))}
                </div>
            ) : null}
            <div className="flex shrink-0 items-center justify-center gap-1.5">
                <PopButton label={micOn ? "Mute" : "Unmute"} danger={!micOn} onClick={onToggleMic}>
                    {micOn ? <Mic className="h-4 w-4" /> : <MicOff className="h-4 w-4" />}
                </PopButton>
                {!audioOnly ? (
                    <PopButton label={camOn ? "Stop camera" : "Start camera"} danger={!camOn} disabled={camDisabled && !camOn} onClick={onToggleCam}>
                        {camOn ? <Video className="h-4 w-4" /> : <VideoOff className="h-4 w-4" />}
                    </PopButton>
                ) : null}
                {sharing ? (
                    <PopButton label="Stop presenting" active onClick={onToggleShare}>
                        <MonitorOff className="h-4 w-4" />
                    </PopButton>
                ) : null}
                <PopButton label={handRaised ? "Lower hand" : "Raise hand"} active={handRaised} onClick={onToggleHand}>
                    <Hand className="h-4 w-4" />
                </PopButton>
                <PopButton label="Back to the meeting tab" onClick={onBackToTab}>
                    <ArrowUpRight className="h-4 w-4" />
                </PopButton>
                <button
                    type="button"
                    onClick={onLeave}
                    aria-label="Leave"
                    title="Leave"
                    className="flex h-8 items-center gap-1 rounded-md bg-red-600 px-2.5 text-xs font-semibold hover:bg-red-500"
                >
                    <PhoneOff className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

function PopTile({ tile, large, audioOnly }: { tile: PopoutTile; large?: boolean; audioOnly: boolean }) {
    const bind = useCallback((el: HTMLVideoElement | null) => {
        if (!el) return;
        if (tile.stream && el.srcObject !== tile.stream) el.srcObject = tile.stream;
        void el.play().catch(() => undefined);
    }, [tile.stream]);
    const showVideo = !audioOnly && tile.videoOn && tile.stream !== null;
    return (
        <div className={cn(
            "relative flex h-full w-full items-center justify-center overflow-hidden rounded-md bg-zinc-800",
            tile.speaking && "ring-2 ring-emerald-400"
        )}>
            {showVideo ? (
                // Muted: the meeting tab plays everyone's audio; this is picture only.
                <video ref={bind} autoPlay playsInline muted className={cn("h-full w-full", large ? "object-contain" : "object-cover")} />
            ) : (
                <div className={cn("flex items-center justify-center rounded-full bg-zinc-700 font-semibold", large ? "h-14 w-14 text-xl" : "h-7 w-7 text-xs")}>
                    {tile.label.charAt(0).toUpperCase()}
                </div>
            )}
            <div className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-gradient-to-t from-black/70 to-transparent px-1.5 py-0.5 text-[10px]">
                <span className="min-w-0 flex-1 truncate">{tile.label}</span>
                {tile.muted ? <MicOff className="h-3 w-3 shrink-0 text-red-400" aria-label="Muted" /> : null}
                {tile.handRaised ? <Hand className="h-3 w-3 shrink-0 text-amber-300" aria-label="Hand raised" /> : null}
            </div>
        </div>
    );
}

function PopButton({ children, label, onClick, danger, active, disabled }: {
    children: React.ReactNode;
    label: string;
    onClick: () => void;
    danger?: boolean;
    active?: boolean;
    disabled?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            aria-label={label}
            title={label}
            className={cn(
                "flex h-8 w-8 items-center justify-center rounded-md disabled:opacity-40",
                danger ? "bg-red-600/90 hover:bg-red-600" : active ? "bg-primary hover:bg-primary/90" : "bg-zinc-800 hover:bg-zinc-700"
            )}
        >
            {children}
        </button>
    );
}
